import { useState, useRef, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMyAttendance, customFetch } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { LogIn, LogOut, Clock, Loader2, ChevronDown } from "lucide-react";
import { formatTime12 } from "@/lib/dateUtils";

/**
 * Attendance header widget — shows in the AppShell top bar for all employees.
 * Displays current check-in status, time, and a dropdown with check-in / check-out actions.
 */
export function AttendanceHeaderWidget() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const today = new Date().toISOString().split("T")[0];
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkOutLoading, setCheckOutLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const attendanceQuery = useGetMyAttendance({ from: today, to: today });
  const records = attendanceQuery.data ?? [];
  const todayRecord = records.find((r) => r.date === today);
  const isCheckedIn = !!todayRecord?.checkIn;
  const isCheckedOut = !!todayRecord?.checkOut;

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const handleCheckIn = async () => {
    setCheckInLoading(true);
    try {
      const clientTime = new Date().toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" });
      await customFetch("/api/hr/me/attendance/check-in", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checkIn: clientTime }),
      });
      await qc.invalidateQueries({ queryKey: attendanceQuery.queryKey });
      qc.invalidateQueries({ queryKey: ["/api/hr/attendance"] });
      qc.invalidateQueries({ queryKey: ["my-salary-tracker"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "employee-stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard/employee-stats"] });
      toast({
        title: "Checked in successfully",
        description: formatTime12(clientTime),
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Check-in failed", variant: "destructive" });
    } finally {
      setCheckInLoading(false);
      setDropdownOpen(false);
    }
  };

  const handleCheckOut = async () => {
    setCheckOutLoading(true);
    try {
      const clientTime = new Date().toLocaleTimeString("en-IN", { hour12: false, hour: "2-digit", minute: "2-digit" });
      await customFetch("/api/hr/me/attendance/check-out", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ checkOut: clientTime }),
      });
      await qc.invalidateQueries({ queryKey: attendanceQuery.queryKey });
      qc.invalidateQueries({ queryKey: ["/api/hr/attendance"] });
      qc.invalidateQueries({ queryKey: ["my-salary-tracker"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "employee-stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard/employee-stats"] });
      toast({
        title: "Checked out successfully",
        description: formatTime12(clientTime),
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Check-out failed", variant: "destructive" });
    } finally {
      setCheckOutLoading(false);
      setDropdownOpen(false);
    }
  };

  // Status label for the header pill
  const statusLabel = !isCheckedIn
    ? "Not Checked In"
    : isCheckedOut
      ? "Day Complete"
      : "Working";

  const statusColor = !isCheckedIn
    ? "bg-amber-400/90 text-amber-900"
    : isCheckedOut
      ? "bg-emerald-400/90 text-emerald-900"
      : "bg-emerald-400/90 text-emerald-900";

  const dotColor = !isCheckedIn
    ? "bg-amber-500"
    : isCheckedOut
      ? "bg-emerald-500"
      : "bg-emerald-500 animate-pulse";

  // Compact time string for the header
  const timeLabel = isCheckedIn
    ? `since ${formatTime12(todayRecord?.checkIn)}`
    : null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger pill */}
      <button
        onClick={() => setDropdownOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors hover:opacity-90 bg-muted text-foreground"
        title="Attendance"
      >
        <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dotColor}`} />
        <span className="hidden sm:inline max-w-[120px] truncate">{statusLabel}</span>
        {timeLabel && <span className="hidden md:inline text-muted-foreground ml-0.5 max-w-[100px] truncate">{timeLabel}</span>}
        <ChevronDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      </button>

      {/* Dropdown */}
      {dropdownOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-card dark:bg-slate-800 rounded-xl shadow-2xl border border-border dark:border-slate-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-muted/50 dark:bg-slate-900 border-b border-border dark:border-slate-700">
            <p className="text-[10px] font-semibold text-muted-foreground dark:text-slate-500 uppercase tracking-wider mb-1">
              Attendance
            </p>
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor}`}>
                {statusLabel}
              </span>
            </div>
          </div>

          {/* Status details */}
          <div className="px-4 py-3 space-y-1.5 text-sm border-b border-border dark:border-slate-700">
            {isCheckedIn && (
              <div className="flex items-center gap-2 text-foreground dark:text-slate-200">
                <LogIn className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                <span className="text-muted-foreground dark:text-slate-400">Checked in at</span>
                <span className="font-mono font-medium">{formatTime12(todayRecord?.checkIn)}</span>
              </div>
            )}
            {isCheckedOut && (
              <div className="flex items-center gap-2 text-foreground dark:text-slate-200">
                <LogOut className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                <span className="text-muted-foreground dark:text-slate-400">Checked out at</span>
                <span className="font-mono font-medium">{formatTime12(todayRecord?.checkOut)}</span>
              </div>
            )}
            {!isCheckedIn && !isCheckedOut && (
              <div className="flex items-center gap-2 text-muted-foreground dark:text-slate-400">
                <Clock className="h-3.5 w-3.5 flex-shrink-0" />
                <span>You have not checked in today</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="py-1">
            {!isCheckedIn && (
              <button
                onClick={handleCheckIn}
                disabled={checkInLoading}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left hover:bg-muted/50 dark:hover:bg-slate-700 transition-colors text-emerald-700 dark:text-emerald-400 font-medium disabled:opacity-50"
              >
                {checkInLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                Check In
              </button>
            )}
            {isCheckedIn && !isCheckedOut && (
              <button
                onClick={handleCheckOut}
                disabled={checkOutLoading}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left hover:bg-muted/50 dark:hover:bg-slate-700 transition-colors text-red-600 dark:text-red-400 font-medium disabled:opacity-50"
              >
                {checkOutLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                Check Out
              </button>
            )}
            {isCheckedOut && (
              <div className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-muted-foreground dark:text-slate-500">
                <Clock className="h-4 w-4" />
                Day is complete
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
